import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  message,
  Modal,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  LinkOutlined,
  ReloadOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import GooglePlacesAutocompleteInput from "common/GooglePlacesAutocompleteInput";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { fetchWorkspace } from "services/workspaceService";
import identitySubscriptionService from "services/subscriptions";
import paymentMethodsService from "services/paymentMethodsService";
import partnersService from "services/partnersService";
import useAppConfig from "hooks/access-control/useAppConfig";
import {
  formatDate,
  planLabel,
  readable,
  subscriptionLabel,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles } from "./colors";
import { oCareSubscriptionFeatureLabels } from "./subscriptionFeatures";
import { addressFieldsFromPlaceDetails } from "utils/googlePlaces";

const { Text } = Typography;

const STRIPE_JS_URL = "https://js.stripe.com/v3/";
let stripeJsPromise;

const loadStripeJs = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Stripe can only be loaded in the browser."));
  }
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (stripeJsPromise) return stripeJsPromise;

  stripeJsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Stripe));
      existing.addEventListener("error", () => reject(new Error("Stripe.js failed to load.")));
      return;
    }

    const script = document.createElement("script");
    script.src = STRIPE_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.Stripe);
    script.onerror = () => reject(new Error("Stripe.js failed to load."));
    document.body.appendChild(script);
  });

  return stripeJsPromise;
};

const stripeCardOptions = {
  hidePostalCode: true,
  style: {
    base: {
      color: "#0F172A",
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: "16px",
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: "#94A3B8",
      },
    },
    invalid: {
      color: "#D9291C",
      iconColor: "#D9291C",
    },
  },
};

const pageContent = (result) => (Array.isArray(result) ? result : result?.content || []);

const money = (value, currency = "USD") => {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return String(value);

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(number);
};

const moneyNumber = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value.replace(/[^0-9.-]/g, ""));
  return Number(value || 0);
};

const firstString = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};
const sameText = (left, right) =>
  Boolean(firstString(left) && firstString(right) && firstString(left).toUpperCase() === firstString(right).toUpperCase());

const normalizeBgpCode = (value = "") =>
  String(value || "").replace(/[^a-zA-Z0-9]/g, "").trim().toUpperCase();

const normalizeCouponCodes = (value = "") =>
  Array.from(
    new Set(
      String(value || "")
        .split(/[\s,;]+/)
        .map((item) => item.replace(/[^a-zA-Z0-9_-]/g, "").trim().toUpperCase())
        .filter(Boolean),
    ),
  );

const couponCodesText = (codes = []) => {
  if (Array.isArray(codes)) return codes.filter(Boolean).join(", ");
  return normalizeCouponCodes(codes).join(", ");
};

const sectionTitle = (title) => (
  <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
    {title}
  </Text>
);

const planRank = (plan) => {
  const value = `${plan?.code || ""} ${plan?.name || ""}`.toUpperCase();
  if (value.includes("FREE")) return 0;
  if (value.includes("STANDARD")) return 1;
  if (value.includes("PREMIUM")) return 2;
  return 3;
};

const sortPlans = (items = []) =>
  [...items].sort((left, right) => {
    const rankDiff = planRank(left) - planRank(right);
    if (rankDiff) return rankDiff;
    return planLabel(left).localeCompare(planLabel(right));
  });

const planCodeOf = (plan) => firstString(plan?.code, plan?.subscriptionPlanCode);
const planIdOf = (plan) => firstString(plan?.id, plan?.subscriptionPlanId);
const planNameOf = (plan) => firstString(plan?.name, plan?.subscriptionPlanName);
const planDescription = (plan) => {
  const description = firstString(plan?.description);
  if (planRank(plan) === 1 && /^free(?: business)? subscription plan\b/i.test(description)) {
    return "Complete AFH care operations, compliance, documentation, staffing, eMAR, eFax, and website tools.";
  }
  return description || readable(plan?.subscriptionType, "Subscription type");
};
const isFreePlanCode = (planCode) => String(planCode || "").toUpperCase().includes("FREE");
const isFreePlan = (plan) => isFreePlanCode(planCodeOf(plan));
const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";
const activeAccountUser = (account, fallbackUser) => {
  const users = account?.users || [];
  const active = users.find((member) => {
    const status = String(member?.membershipStatus || "").toUpperCase();
    return status === "ACTIVE" || status === "ACCEPTED" || status === "";
  });
  return active?.user || fallbackUser || {};
};
const userName = (user, fallback = "User") => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.name || user?.username || user?.email || user?.id || fallback;
};
const paymentMethodId = (method) =>
  method?.id || method?.stripePaymentMethodId || method?.paymentMethodId || "";
const isDefaultOutgoing = (method) =>
  Boolean(
    method?.defaultOutgoingPaymentMethod ||
      method?.isDefaultOutgoingPaymentMethod ||
      method?.default_outgoing_payment_method ||
      method?.is_default_outgoing_payment_method ||
      method?.isDefaultForCurrency ||
      method?.is_default_for_currency ||
      method?.default,
  );
const maskedPaymentMethod = (method) => {
  if (!method) return "Default payment method";
  const brand = method?.merchant || method?.brand || method?.cardBrand || "Card";
  const last4 = method?.last4 || method?.cardLast4 || method?.accountLast4;
  return last4 ? `${brand} •••• ${last4}` : brand;
};
const paymentMethodExpiry = (method) => {
  const month = method?.expiryMonth || method?.expMonth;
  const year = method?.expiryYear || method?.expYear;
  if (!month || !year) return "";
  return `${String(month).padStart(2, "0")}/${String(year).slice(-2)}`;
};
const normalizeBillingCountry = (value = "") =>
  String(value || "").replace(/[^a-zA-Z]/g, "").trim().toUpperCase().slice(0, 2);
const normalizeBillingState = (value = "") =>
  String(value || "").replace(/[^a-zA-Z]/g, "").trim().toUpperCase().slice(0, 3);
const normalizeBillingPostalCode = (value = "") =>
  String(value || "").replace(/[^a-zA-Z0-9 -]/g, "").trim().toUpperCase().slice(0, 12);
const normalizeBillingText = (value = "", maxLength = 120) =>
  String(value || "").replace(/[<>]/g, "").trim().slice(0, maxLength);
const paymentMethodBillingLocation = (method) => ({
  addressLine1: normalizeBillingText(
    method?.billingAddressLine1 ||
      method?.billing_address_line1 ||
      method?.addressLine1 ||
      method?.address_line1 ||
      method?.billingAddress?.addressLine1 ||
      method?.billingAddress?.line1 ||
      method?.billing_address?.addressLine1 ||
      method?.billing_address?.line1 ||
      method?.billing_details?.address?.line1,
  ),
  city: normalizeBillingText(
    method?.billingCity ||
      method?.billing_city ||
      method?.city ||
      method?.billingAddress?.city ||
      method?.billing_address?.city ||
      method?.billing_details?.address?.city,
    80,
  ),
  county: normalizeBillingText(
    method?.billingCounty ||
      method?.billing_county ||
      method?.county ||
      method?.billingAddress?.county ||
      method?.billing_address?.county,
    120,
  ),
  country: normalizeBillingCountry(
    method?.billingCountry ||
      method?.billing_country ||
      method?.country ||
      method?.billingAddress?.country ||
      method?.billingAddress?.countryCode ||
      method?.billing_address?.country ||
      method?.billing_address?.countryCode ||
      method?.billing_address?.country_code ||
      method?.billing_details?.address?.country,
  ),
  state: normalizeBillingState(
    method?.billingState ||
      method?.billing_state ||
      method?.state ||
      method?.stateCode ||
      method?.state_code ||
      method?.billingAddress?.state ||
      method?.billingAddress?.stateCode ||
      method?.billing_address?.state ||
      method?.billing_address?.stateCode ||
      method?.billing_address?.state_code ||
      method?.billing_details?.address?.state,
  ),
  postalCode: normalizeBillingPostalCode(
    method?.billingPostalCode ||
      method?.billing_postal_code ||
      method?.postalCode ||
      method?.postal_code ||
      method?.zipCode ||
      method?.zip_code ||
      method?.billingAddress?.postalCode ||
      method?.billingAddress?.zipCode ||
      method?.billing_address?.postalCode ||
      method?.billing_address?.postal_code ||
      method?.billing_address?.zipCode ||
      method?.billing_address?.zip_code ||
      method?.billing_details?.address?.postal_code,
  ),
});
const billingLocationText = (method) => {
  const location = paymentMethodBillingLocation(method);
  const cityLine = [location.city, location.state, location.postalCode].filter(Boolean).join(", ");
  return [location.addressLine1, cityLine, location.country].filter(Boolean).join(" • ");
};
const moneyValue = (value) => Number(moneyNumber(value).toFixed(2));
const newChargeLineId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `charge-line-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};
const chargeConfigValue = (charge, amount) => {
  const value = moneyNumber(charge?.value);
  if (!value) return 0;
  return String(charge?.valueType || "").toUpperCase() === "PERCENTAGE"
    ? moneyValue(amount * value)
    : moneyValue(value);
};
const chargeConfigRate = (charge) =>
  String(charge?.valueType || "").toUpperCase() === "PERCENTAGE"
    ? moneyNumber(charge?.value)
    : null;
const ratePercentText = (rate) => {
  if (rate === undefined || rate === null || rate === "") return "";
  const number = moneyNumber(rate);
  if (!number) return "";
  return `${(number * 100).toFixed(2).replace(/\.?0+$/, "")}%`;
};
const SUBSCRIPTION_CHARGE_APP = "OLIVEEB_CARE_SERVICE";
const SUBSCRIPTION_CHARGE_STAGE = "OLIVEEB_CARE_SERVICE_ALL";
const PAYMENT_PROCESSOR_VARIABLE_FEE_CODE = "PAYMENT_PROCESSOR_VARIABLE_FEE";
const PAYMENT_PROCESSOR_FIXED_FEE_CODE = "PAYMENT_PROCESSOR_FIXED_FEE";
const normalizeChargeCode = (value) => String(value || "").trim().toUpperCase();
const normalizeChargeApp = (value) => String(value || "").trim().toUpperCase().replace(/-/g, "_");
const isPaymentProcessorCharge = (charge) =>
  [PAYMENT_PROCESSOR_VARIABLE_FEE_CODE, PAYMENT_PROCESSOR_FIXED_FEE_CODE].includes(normalizeChargeCode(charge?.code));
const chargeMatchesSubscriptionScope = (charge) => {
  const app = normalizeChargeApp(charge?.app);
  const stage = normalizeChargeCode(charge?.stage);
  const currency = normalizeChargeCode(charge?.currency);
  const target = normalizeChargeCode(charge?.chargeTarget);
  const status = normalizeChargeCode(charge?.status);
  return (
    isPaymentProcessorCharge(charge) &&
    (!status || status === "ACTIVE") &&
    (!currency || currency === "USD") &&
    (!target || target === "CHECKOUT") &&
    app === SUBSCRIPTION_CHARGE_APP &&
    stage === SUBSCRIPTION_CHARGE_STAGE
  );
};
const resolveProcessorCharges = (charges = []) => {
  const list = Array.isArray(charges) ? charges : [];
  const candidates = list.filter(chargeMatchesSubscriptionScope);
  return {
    variable: candidates.find((charge) => normalizeChargeCode(charge?.code) === PAYMENT_PROCESSOR_VARIABLE_FEE_CODE) || null,
    fixed: candidates.find((charge) => normalizeChargeCode(charge?.code) === PAYMENT_PROCESSOR_FIXED_FEE_CODE) || null,
  };
};
const chargeLineRateText = (line) => {
  const valueType = String(line?.valueType || "").toUpperCase();
  if (valueType === "PERCENTAGE" && line?.rate !== undefined && line?.rate !== null) {
    return ratePercentText(line.rate);
  }
  if (valueType === "FIXED" && line?.fixedAmount !== undefined && line?.fixedAmount !== null) {
    return money(line.fixedAmount);
  }
  return "";
};
const chargeLineLabel = (line) => {
  const label = firstString(line?.name, line?.code, "Charge");
  const rate = chargeLineRateText(line);
  return rate ? `${label} (${rate})` : label;
};
const sortedChargeLines = (lines = []) =>
  (Array.isArray(lines) ? lines : [])
    .filter((line) => line && moneyNumber(line.amount) !== 0)
    .slice()
    .sort((a, b) => {
      const aOrder = Number.isFinite(Number(a?.displayOrder)) ? Number(a.displayOrder) : 999;
      const bOrder = Number.isFinite(Number(b?.displayOrder)) ? Number(b.displayOrder) : 999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(firstString(a?.name, a?.code)).localeCompare(String(firstString(b?.name, b?.code)));
    });
const feesAndTaxesTotal = (lines = []) =>
  moneyValue(
    (Array.isArray(lines) ? lines : [])
      .filter((line) => ["FEE", "TAX"].includes(normalizeChargeCode(line?.category)))
      .reduce((sum, line) => sum + moneyNumber(line?.amount), 0),
  );
const subscriptionChargePayload = (preview = {}) => {
  const breakdown = preview?.chargeBreakdown || {};
  const chargeDetails = {
    currentChargeAmount: moneyNumber(preview?.currentChargeAmount),
    originalChargeAmount: moneyNumber(preview?.originalChargeAmount),
    discountedChargeAmount: moneyNumber(preview?.discountedChargeAmount),
    couponCodes: Array.isArray(preview?.couponCodes) ? preview.couponCodes : [],
    couponDetails: Array.isArray(preview?.couponDetails) ? preview.couponDetails : [],
    appliedCoupons: Array.isArray(preview?.appliedCoupons) ? preview.appliedCoupons : [],
    rejectedCoupons: Array.isArray(preview?.rejectedCoupons) ? preview.rejectedCoupons : [],
    couponDiscountTotal: moneyNumber(preview?.couponDiscountTotal),
    serviceFee: moneyNumber(breakdown.serviceFee),
    oliveebServiceFeeRate: breakdown.oliveebServiceFeeRate ?? null,
    oliveebServiceFee: moneyNumber(breakdown.oliveebServiceFee),
    paymentProcessorFeeRate: breakdown.paymentProcessorFeeRate ?? null,
    paymentProcessorFee: moneyNumber(breakdown.paymentProcessorFee),
    paymentFeeConstant: moneyNumber(breakdown.paymentFeeConstant),
    totalPaymentProcessorFee: moneyNumber(breakdown.totalPaymentProcessorFee),
    federalTax: moneyNumber(breakdown.federalTax),
    federalTaxRate: breakdown.federalTaxRate ?? null,
    stateTax: moneyNumber(breakdown.stateTax),
    stateTaxRate: breakdown.stateTaxRate ?? null,
    totalCharge: moneyNumber(breakdown.totalCharge),
    chargeLines: Array.isArray(breakdown.chargeLines) ? breakdown.chargeLines : [],
  };
  const hasChargeDetails =
    chargeDetails.chargeLines.length ||
    chargeDetails.couponCodes.length ||
    chargeDetails.couponDetails.length ||
    chargeDetails.appliedCoupons.length ||
    chargeDetails.couponDiscountTotal > 0;
  return hasChargeDetails
    ? {
        ...chargeDetails,
        paymentChargeDetails: chargeDetails,
      }
    : {};
};
const couponDescription = (coupon = {}) => {
  const type = String(coupon?.discountType || "").toUpperCase();
  const value = moneyNumber(coupon?.discountValue);
  const amount = money(coupon?.discountAmount);
  const label = firstString(coupon?.description, coupon?.name, coupon?.code, "Coupon");
  if (type === "PERCENTAGE" && value) {
    return `${label} (${value}% off): -${amount}`;
  }
  return `${label}: -${amount}`;
};
const buildSubscriptionChargeBreakdown = (amount, charges = []) => {
  const normalizedAmount = moneyValue(amount);
  let serviceFee = 0;
  let oliveebServiceFee = 0;
  let oliveebServiceFeeRate = null;
  let federalTax = 0;
  let federalTaxRate = null;
  let stateTax = 0;
  let stateTaxRate = null;
  const chargeLines = [];

  charges.forEach((charge) => {
    const value = chargeConfigValue(charge, normalizedAmount);
    if (!value) return;
    const category = String(charge?.category || "").toUpperCase();
    const code = String(charge?.code || "").toUpperCase();
    const valueType = String(charge?.valueType || "").toUpperCase();
    const rate = chargeConfigRate(charge);

    if (isPaymentProcessorCharge(charge)) {
      return;
    }

    chargeLines.push({
      chargeId: firstString(charge?.id, charge?.chargeId) || newChargeLineId(),
      code,
      name: firstString(charge?.name, code),
      description: firstString(charge?.description) || null,
      category,
      valueType,
      ...(rate !== null ? { rate } : {}),
      ...(valueType === "FIXED" ? { fixedAmount: moneyValue(charge?.value) } : {}),
      applyOn: firstString(charge?.applyOn, "FEE_TAX_BASE"),
      amount: moneyValue(value),
      displayOrder: charge?.displayOrder ?? null,
      country: firstString(charge?.country) || null,
      state: firstString(charge?.state) || null,
      county: firstString(charge?.county) || null,
      city: firstString(charge?.city) || null,
    });

    if (category === "TAX") {
      if (code.includes("STATE")) {
        stateTax = moneyValue(stateTax + value);
        stateTaxRate = chargeConfigRate(charge);
      } else {
        federalTax = moneyValue(federalTax + value);
        federalTaxRate = chargeConfigRate(charge);
      }
    } else if (code.includes("OLIVEEB")) {
      oliveebServiceFee = moneyValue(oliveebServiceFee + value);
      oliveebServiceFeeRate = chargeConfigRate(charge);
    } else {
      serviceFee = moneyValue(serviceFee + value);
    }
  });

  const chargeableAmount = moneyValue(normalizedAmount + serviceFee + oliveebServiceFee + federalTax + stateTax);
  const { variable: processorVariableCharge, fixed: processorFixedCharge } = resolveProcessorCharges(charges);
  const paymentProcessorFeeRate = chargeConfigRate(processorVariableCharge);
  const paymentFeeConstant = moneyValue(processorFixedCharge?.value);
  const paymentProcessorFee = chargeableAmount > 0 ? moneyValue(chargeableAmount * paymentProcessorFeeRate) : 0;
  const effectivePaymentFeeConstant = chargeableAmount > 0 ? paymentFeeConstant : 0;
  const totalPaymentProcessorFee = moneyValue(paymentProcessorFee + effectivePaymentFeeConstant);
  const totalCharge = moneyValue(chargeableAmount + totalPaymentProcessorFee);
  if (paymentProcessorFee) {
    chargeLines.push({
      chargeId: firstString(processorVariableCharge?.id, processorVariableCharge?.chargeId) || newChargeLineId(),
      code: PAYMENT_PROCESSOR_VARIABLE_FEE_CODE,
      name: firstString(processorVariableCharge?.name, "Payment Processor Variable Fee"),
      description: firstString(processorVariableCharge?.description) || null,
      category: "FEE",
      valueType: "PERCENTAGE",
      rate: paymentProcessorFeeRate,
      applyOn: firstString(processorVariableCharge?.applyOn, "FEE_TAX_BASE"),
      amount: paymentProcessorFee,
      displayOrder: processorVariableCharge?.displayOrder ?? null,
      country: firstString(processorVariableCharge?.country) || null,
      state: firstString(processorVariableCharge?.state) || null,
      county: firstString(processorVariableCharge?.county) || null,
      city: firstString(processorVariableCharge?.city) || null,
    });
  }
  if (effectivePaymentFeeConstant) {
    chargeLines.push({
      chargeId: firstString(processorFixedCharge?.id, processorFixedCharge?.chargeId) || newChargeLineId(),
      code: PAYMENT_PROCESSOR_FIXED_FEE_CODE,
      name: firstString(processorFixedCharge?.name, "Payment Processor Fixed Fee"),
      description: firstString(processorFixedCharge?.description) || null,
      category: "FEE",
      valueType: "FIXED",
      fixedAmount: paymentFeeConstant,
      applyOn: firstString(processorFixedCharge?.applyOn, "FEE_TAX_BASE"),
      amount: effectivePaymentFeeConstant,
      displayOrder: processorFixedCharge?.displayOrder ?? null,
      country: firstString(processorFixedCharge?.country) || null,
      state: firstString(processorFixedCharge?.state) || null,
      county: firstString(processorFixedCharge?.county) || null,
      city: firstString(processorFixedCharge?.city) || null,
    });
  }

  return {
    serviceFee,
    oliveebServiceFee,
    oliveebServiceFeeRate,
    federalTax,
    federalTaxRate,
    stateTax,
    stateTaxRate,
    chargeableAmount,
    paymentProcessorFeeRate,
    paymentProcessorFee,
    paymentFeeConstant,
    totalPaymentProcessorFee,
    totalCharge,
    chargeLines,
  };
};
const planKey = (plan) => firstString(planIdOf(plan), planCodeOf(plan));
const planSignal = (plan) =>
  [
    plan?.code,
    plan?.name,
    plan?.subscriptionPlanCode,
    plan?.subscriptionPlanName,
    plan?.tier,
  ].map((value) => String(value || "").trim()).filter(Boolean).join(" ").toUpperCase();

const normalizePlanFeatureList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.label || item?.name || item?.title || item?.description || "";
      })
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/\r?\n|[;•]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const planProvidedBenefits = (plan) =>
  normalizePlanFeatureList(
    plan?.features ||
      plan?.benefits ||
      plan?.includedServices ||
      plan?.services ||
      plan?.serviceOfferings ||
      plan?.metadata?.features ||
      plan?.metadata?.benefits ||
      plan?.metadata?.includedServices,
  );

const careBusinessPlanBenefits = (plan) => {
  const signal = planSignal(plan);
  if (signal.includes("FREE")) {
    return oCareSubscriptionFeatureLabels("FREE");
  }
  if (signal.includes("STANDARD")) {
    return oCareSubscriptionFeatureLabels("STANDARD");
  }
  if (signal.includes("PREMIUM")) {
    return oCareSubscriptionFeatureLabels("PREMIUM");
  }
  return [];
};

const carePersonalPlanBenefits = (plan) => {
  const signal = planSignal(plan);
  if (signal.includes("FREE")) {
    return [
      "Create and maintain a professional care profile",
      "Browse care and hiring opportunities",
      "Basic application tracking",
      "Limited profile visibility",
    ];
  }
  if (signal.includes("STANDARD")) {
    return [
      "Enhanced professional care profile",
      "Unlimited applications and opportunity tracking",
      "Higher visibility to care organizations",
      "Access to verification and background-check results",
      "Messaging with employers and care teams",
    ];
  }
  if (signal.includes("PREMIUM")) {
    return [
      "Everything in Standard",
      "AI-assisted profile, application, and care task support",
      "Priority profile visibility",
      "Personal professional website",
      "Profile sharing and verification-ready presentation",
      "Advanced job and care opportunity support",
    ];
  }
  return [];
};

const planBenefits = (plan, account) => {
  const provided = planProvidedBenefits(plan);
  if (provided.length) return provided;
  return isPersonalAccount(account) ? carePersonalPlanBenefits(plan) : careBusinessPlanBenefits(plan);
};

const subscriptionContext = (account, actorId) => {
  if (isPersonalAccount(account)) {
    return {
      planType: "PERSONAL",
      subscriberType: "USER",
      subscriberId: actorId,
      organizationId: "",
      ready: Boolean(actorId),
    };
  }

  return {
    planType: "BUSINESS",
    subscriberType: "BUSINESS",
    subscriberId: account?.organizationId || "",
    organizationId: account?.organizationId || "",
    ready: Boolean(account?.organizationId),
  };
};

const planMonthlyPrice = (plan) => money(plan?.monthlyAmount) || "$0.00";
const planAnnualPrice = (plan) => money(plan?.annualAmount) || "$0.00";
const planAnnualizedMonthlyPrice = (plan) => money(moneyNumber(plan?.monthlyAmount) * 12) || "$0.00";
const planAnnualSavings = (plan) => {
  const savings = (moneyNumber(plan?.monthlyAmount) * 12) - moneyNumber(plan?.annualAmount);
  return savings > 0 ? money(savings) : "";
};

const addYears = (date, years) => {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
};

const addMonthsClamped = (date, months) => {
  const next = new Date(date);
  const originalDay = date.getDate();
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(originalDay, lastDay));
  next.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  return next;
};

const subscriptionCycleInfo = (startDate, now = new Date()) => {
  const start = startDate ? new Date(startDate) : null;
  if (!start || !Number.isFinite(start.getTime())) {
    return {
      cycleEndDate: new Date(addMonthsClamped(now, 1).getTime() - 1),
      usedMonths: 1,
    };
  }

  let usedMonths = 1;
  let nextBoundary = addMonthsClamped(start, usedMonths);
  while (nextBoundary.getTime() <= now.getTime() && usedMonths < 120) {
    usedMonths += 1;
    nextBoundary = addMonthsClamped(start, usedMonths);
  }

  return {
    cycleEndDate: new Date(nextBoundary.getTime() - 1),
    usedMonths: Math.min(12, usedMonths),
  };
};

const normalizedPaymentType = (value) => {
  const nextValue = String(value || "").toUpperCase();
  if (nextValue === "MONTHLY" || nextValue === "MONTH") return "MONTHLY";
  if (["ANNUALLY", "ANNUAL", "YEARLY", "YEAR"].includes(nextValue)) return "ANNUALLY";
  return "";
};

const subscriptionPeriod = (paymentType, start = new Date()) => {
  const startDate = new Date(start);
  const endDate = paymentType === "ANNUALLY"
    ? addYears(startDate, 1)
    : addMonthsClamped(startDate, 1);

  return {
    startDate,
    endDate,
  };
};

const newCorrelationId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (char) => {
    const value = Number(char);
    const random = Math.floor(Math.random() * 16);
    return (value ^ (random & (15 >> (value / 4)))).toString(16);
  });
};

const WorkspaceSubscriptionsPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const actorId = user?.id || user?.userId || "";
  const [account, setAccount] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingPlanKey, setCreatingPlanKey] = useState("");
  const [selectedPlanCode, setSelectedPlanCode] = useState("");
  const [selectedPaymentTypes, setSelectedPaymentTypes] = useState({});
  const [chargePreview, setChargePreview] = useState(null);
  const [subscriptionBgpCode, setSubscriptionBgpCode] = useState("");
  const [subscriptionCouponCodes, setSubscriptionCouponCodes] = useState("");
  const [bgpCodeWarning, setBgpCodeWarning] = useState("");
  const [cancelPreview, setCancelPreview] = useState(null);
  const [paymentMethodDialogOpen, setPaymentMethodDialogOpen] = useState(false);
  const [checkingPaymentMethod, setCheckingPaymentMethod] = useState(false);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeError, setStripeError] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [billingCountry, setBillingCountry] = useState("US");
  const [billingState, setBillingState] = useState("");
  const [billingPostalCode, setBillingPostalCode] = useState("");
  const [billingAddressSearch, setBillingAddressSearch] = useState("");
  const [billingAddressLine1, setBillingAddressLine1] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingCounty, setBillingCounty] = useState("");
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const stripeRef = useRef(null);
  const stripeCardRef = useRef(null);
  const cardMountRef = useRef(null);
  const subscriptionSubmitInFlightRef = useRef(false);
  const stripePublishableKey =
    appConfig?.stripePublishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.REACT_APP_GOOGLE_API_KEY ||
    "";

  const loadData = useCallback(async () => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      setDefaultPaymentMethod(null);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      const nextContext = subscriptionContext(nextAccount, actorId);
      setAccount(nextAccount);

      const requests = [
        identitySubscriptionService.plans.list({
          code: "CARE",
          status: "ACTIVE",
          type: nextContext.planType,
          page: 0,
          size: 100,
        }),
      ];

      if (nextContext.ready) {
        requests.push(
          identitySubscriptionService.subscriptions.list({
            subscriberType: nextContext.subscriberType,
            subscriberId: nextContext.subscriberId,
            workspaceId,
            organizationId: nextContext.organizationId || undefined,
            code: "CARE",
            status: "ACTIVE",
            page: 0,
            size: 100,
            sort: "startDate,desc",
            wid: workspaceId,
          }),
        );
      } else {
        requests.push(Promise.resolve({ content: [] }));
      }

      const [plansResult, subscriptionsResult] = await Promise.all(requests);
      const sortedPlans = sortPlans(pageContent(plansResult));
      const nextSubscriptions = pageContent(subscriptionsResult);
      const activeBusinessSubscription =
        nextSubscriptions.find((subscription) => String(subscription?.status || "").toUpperCase() === "ACTIVE") ||
        nextSubscriptions[0] ||
        null;
      const nextCurrentSubscription = activeBusinessSubscription || null;
      const fallbackPaymentType = normalizedPaymentType(
        nextCurrentSubscription?.subscriptionPaymentType || nextSubscriptions[0]?.subscriptionPaymentType,
      );
      const fallbackPlanCode = firstString(
        nextCurrentSubscription?.subscriptionPlanId,
        nextCurrentSubscription?.subscriptionPlan?.id,
        nextCurrentSubscription?.subscriptionPlanCode,
        nextCurrentSubscription?.subscriptionPlan?.code,
        nextCurrentSubscription?.plan?.code,
        nextSubscriptions[0]?.subscriptionPlanId,
        nextSubscriptions[0]?.subscriptionPlan?.id,
        nextSubscriptions[0]?.subscriptionPlanCode,
        nextSubscriptions[0]?.subscriptionPlan?.code,
        nextSubscriptions[0]?.plan?.code,
      );

      setPlans(sortedPlans);
      setSubscriptions(nextSubscriptions);
      setCurrentSubscription(nextCurrentSubscription);
      setSelectedPlanCode("");
      setSubscriptionBgpCode(firstString(nextCurrentSubscription?.bgpCode));
      setSubscriptionCouponCodes(couponCodesText(nextCurrentSubscription?.couponCodes || []));
      if (fallbackPlanCode && fallbackPaymentType) {
        setSelectedPaymentTypes((current) => ({
          ...current,
          [fallbackPlanCode]: fallbackPaymentType,
        }));
      }
    } catch (error) {
      message.error(error?.message || "Failed to load subscription options.");
    } finally {
      setLoading(false);
    }
  }, [actorId, workspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!paymentMethodDialogOpen) return undefined;

    let active = true;
    setStripeReady(false);
    setStripeError("");
    setCardComplete(false);

    if (!stripePublishableKey) {
      setStripeError("Stripe publishable key is not configured.");
      return undefined;
    }

    loadStripeJs()
      .then((Stripe) => {
        if (!active) return;
        const stripe = Stripe(stripePublishableKey);
        const elements = stripe.elements();
        const card = elements.create("card", stripeCardOptions);
        stripeRef.current = stripe;
        stripeCardRef.current = card;

        card.on("change", (event) => {
          setCardComplete(Boolean(event.complete));
          setStripeError(event.error?.message || "");
        });

        if (cardMountRef.current) {
          card.mount(cardMountRef.current);
          setStripeReady(true);
        }
      })
      .catch((error) => {
        if (active) setStripeError(error?.message || "Stripe.js failed to load.");
      });

    return () => {
      active = false;
      if (stripeCardRef.current) {
        stripeCardRef.current.destroy();
        stripeCardRef.current = null;
      }
      stripeRef.current = null;
    };
  }, [paymentMethodDialogOpen, stripePublishableKey]);

  const availableSubscriptions = useMemo(() => {
    const byId = new Map();
    subscriptions.forEach((subscription) => {
      if (subscription?.id) byId.set(subscription.id, subscription);
    });
    if (currentSubscription?.id) {
      byId.set(currentSubscription.id, currentSubscription);
    }
    return Array.from(byId.values());
  }, [currentSubscription, subscriptions]);

  const currentPlanCode = firstString(
    currentSubscription?.subscriptionPlanCode,
    currentSubscription?.subscriptionPlan?.code,
    currentSubscription?.plan?.code,
  );
  const currentPlanId = firstString(
    currentSubscription?.subscriptionPlanId,
    currentSubscription?.subscriptionPlan?.id,
    currentSubscription?.plan?.id,
  );
  const currentPlanName = firstString(
    currentSubscription?.subscriptionPlanName,
    currentSubscription?.subscriptionPlan?.name,
    currentSubscription?.plan?.name,
  );
  const currentPlanKey = currentPlanId || currentPlanCode;
  const currentSubscriptionId = firstString(currentSubscription?.id, currentSubscription?.subscriptionId);
  const currentPaymentType = normalizedPaymentType(currentSubscription?.subscriptionPaymentType);
  const defaultFreePlan = useMemo(() => plans.find(isFreePlan) || null, [plans]);
  const effectiveCurrentPlanCode = currentPlanCode || firstString(defaultFreePlan?.code);
  const includedPlanIsActive = Boolean(!currentSubscription && defaultFreePlan);
  const currentPlan = useMemo(
    () =>
      plans.find((plan) => {
        const planCode = planCodeOf(plan);
        const planId = planIdOf(plan);
        const name = planNameOf(plan);
        return Boolean(
          (currentPlanId && planId === currentPlanId) ||
          (currentPlanCode && sameText(planCode, currentPlanCode)) ||
          (currentPlanName && sameText(name, currentPlanName))
        );
      }) ||
      currentSubscription?.subscriptionPlan ||
      currentSubscription?.plan ||
      defaultFreePlan ||
      null,
    [currentPlanCode, currentPlanId, currentPlanName, currentSubscription, defaultFreePlan, plans],
  );
  const selectedPlan = useMemo(
    () =>
      plans.find((plan) => {
        const key = planKey(plan);
        return (
          key === selectedPlanCode ||
          firstString(plan?.id) === selectedPlanCode ||
          firstString(plan?.code) === selectedPlanCode
        );
      }) || null,
    [plans, selectedPlanCode],
  );
  const selectedPaymentType = selectedPlanCode
    ? selectedPaymentTypes[selectedPlanCode] ||
      (selectedPlanCode === currentPlanKey && currentPaymentType ? currentPaymentType : "MONTHLY")
    : "MONTHLY";
  const selectedBgpCode = normalizeBgpCode(subscriptionBgpCode);
  const selectedCouponCodes = normalizeCouponCodes(subscriptionCouponCodes);
  const subscriberContext = useMemo(
    () => subscriptionContext(account, actorId),
    [account, actorId],
  );
  const currentBgpCode = normalizeBgpCode(currentSubscription?.bgpCode);
  const currentCouponCodes = normalizeCouponCodes(
    couponCodesText(currentSubscription?.couponCodes || currentSubscription?.paymentChargeDetails?.couponCodes || []),
  );
  const selectedCouponCodesText = couponCodesText(selectedCouponCodes);
  const currentCouponCodesText = couponCodesText(currentCouponCodes);
  const hasSaveChanges = Boolean(
    subscriberContext.ready &&
      selectedPlanCode &&
      !isFreePlanCode(selectedPlanCode) &&
      (
        selectedPlanCode !== (currentPlanKey || effectiveCurrentPlanCode) ||
        selectedPaymentType !== currentPaymentType ||
        selectedBgpCode !== currentBgpCode ||
        selectedCouponCodesText !== currentCouponCodesText
      ),
  );

  const paymentMethodOwnerParams = useCallback(() => {
    if (!account) return null;
    if (isPersonalAccount(account)) {
      const ownerUser = activeAccountUser(account, user);
      return {
        actorId,
        userId: ownerUser?.id || actorId,
        workspaceId,
      };
    }
    if (!account.organizationId) return null;
    return {
      actorId,
      organizationId: account.organizationId,
      workspaceId,
    };
  }, [account, actorId, user, workspaceId]);

  const loadDefaultPaymentMethod = useCallback(async () => {
    const ownerParams = paymentMethodOwnerParams();
    if (!ownerParams) return null;
    const methods = await paymentMethodsService.outgoing.list({
      ...ownerParams,
      page: 0,
      size: 50,
    });
    const listedDefaultMethod = methods.find(isDefaultOutgoing) || null;
    const defaultMethodId = paymentMethodId(listedDefaultMethod);
    const defaultMethod = defaultMethodId
      ? await paymentMethodsService.outgoing.get({
          actorId,
          methodId: defaultMethodId,
        }).catch(() => listedDefaultMethod)
      : listedDefaultMethod;
    setDefaultPaymentMethod(defaultMethod);
    return defaultMethod;
  }, [actorId, paymentMethodOwnerParams]);

  const ensureDefaultPaymentMethod = async () => {
    if (defaultPaymentMethod) return defaultPaymentMethod;
    setCheckingPaymentMethod(true);
    try {
      const defaultMethod = await loadDefaultPaymentMethod();
      if (defaultMethod) return defaultMethod;
      setPaymentMethodDialogOpen(true);
      return null;
    } catch (error) {
      setPaymentMethodDialogOpen(true);
      message.error(error?.message || "Unable to check payment methods.");
      return null;
    } finally {
      setCheckingPaymentMethod(false);
    }
  };

  const closePaymentMethodDialog = () => {
    if (addingPaymentMethod) return;
    setPaymentMethodDialogOpen(false);
    setCardComplete(false);
    setStripeError("");
    setBillingCountry("US");
    setBillingState("");
    setBillingPostalCode("");
    setBillingAddressSearch("");
    setBillingAddressLine1("");
    setBillingCity("");
    setBillingCounty("");
  };

  const addQuickPaymentMethod = async () => {
    if (!stripeRef.current || !stripeCardRef.current) {
      message.error("Stripe card entry is not ready yet.");
      return;
    }
    if (!cardComplete) {
      message.error("Enter the full card details first.");
      return;
    }
    const normalizedBillingCountry = normalizeBillingCountry(billingCountry);
    const normalizedBillingState = normalizeBillingState(billingState);
    const normalizedBillingPostalCode = normalizeBillingPostalCode(billingPostalCode);
    if (!normalizedBillingPostalCode) {
      message.error("Enter the billing ZIP or postal code.");
      return;
    }
    if (normalizedBillingCountry === "US" && !normalizedBillingState) {
      message.error("Enter the billing state.");
      return;
    }

    const ownerParams = paymentMethodOwnerParams();
    if (!ownerParams) {
      message.error("Payment methods cannot be attached for this workspace yet.");
      return;
    }

    try {
      setAddingPaymentMethod(true);
      const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
        type: "card",
        card: stripeCardRef.current,
        billing_details: {
          name: userName(user, "") || undefined,
          email: user?.email || undefined,
          address: {
            country: normalizedBillingCountry,
            line1: billingAddressLine1 || undefined,
            city: billingCity || undefined,
            postal_code: normalizedBillingPostalCode,
            state: normalizedBillingState || undefined,
          },
        },
      });

      if (error || !paymentMethod?.id) {
        setStripeError(error?.message || "Stripe could not create a payment method.");
        message.error(error?.message || "Failed to create payment method.");
        return;
      }

      const attachedMethod = await paymentMethodsService.outgoing.attach({
        ...ownerParams,
        paymentMethodId: paymentMethod.id,
        billingAddressLine1,
        billingCity,
        billingCountry: normalizedBillingCountry,
        billingCounty,
        billingPostalCode: normalizedBillingPostalCode,
        billingState: normalizedBillingState,
      });
      const attachedMethods = await paymentMethodsService.outgoing.list({
        ...ownerParams,
        page: 0,
        size: 50,
      });
      const attachedRecord = attachedMethods.find((method) =>
        paymentMethodId(method) === paymentMethodId(attachedMethod) ||
        paymentMethodId(method) === paymentMethod.id,
      );
      const methodId = paymentMethodId(attachedRecord || attachedMethod);
      if (methodId && !isDefaultOutgoing(attachedRecord || attachedMethod)) {
        await paymentMethodsService.outgoing.setDefault({
          ...ownerParams,
          methodId,
        });
      }
      const defaultMethod = await loadDefaultPaymentMethod();
      setPaymentMethodDialogOpen(false);
      setCardComplete(false);
      setStripeError("");
      setBillingCountry("US");
      setBillingState("");
      setBillingPostalCode("");
      setBillingAddressSearch("");
      setBillingAddressLine1("");
      setBillingCity("");
      setBillingCounty("");
      message.success(defaultMethod ? "Payment method added and set as default." : "Payment method added.");
    } catch (error) {
      message.error(error?.message || "Failed to add payment method.");
    } finally {
      setAddingPaymentMethod(false);
    }
  };

  const saveOrganizationSubscription = async (subscriptionId) => {
    if (!subscriptionId) return false;

    try {
      const subscription = await identitySubscriptionService.subscriptions.get(subscriptionId);
      setCurrentSubscription(subscription || null);
      message.success("Subscription saved to the attached organization.");
      return true;
    } catch (error) {
      message.error(error?.message || "Failed to save subscription.");
      return false;
    }
  };

  const applyBillingPlace = ({ details, prediction }) => {
    const resolvedAddress = details?.formatted_address || prediction?.description || billingAddressSearch || "";
    setBillingAddressSearch(resolvedAddress);
    if (!details) return;

    const fields = addressFieldsFromPlaceDetails(details);
    setBillingAddressLine1(normalizeBillingText(fields.addressLine1));
    setBillingCity(normalizeBillingText(fields.city, 80));
    setBillingCounty(normalizeBillingText(fields.county, 120));
    setBillingCountry(normalizeBillingCountry(fields.country));
    setBillingState(normalizeBillingState(fields.state));
    setBillingPostalCode(normalizeBillingPostalCode(fields.zipCode));
  };

  const validateBgpCodeForSubmission = async (bgpCode = "") => {
    const normalizedCode = String(bgpCode || "").trim();
    if (!normalizedCode) {
      setBgpCodeWarning("");
      return true;
    }

    try {
      const result = await partnersService.list({ page: 0, size: 500 });
      const partner = pageContent(result).find(
        (item) => String(item?.referralCode || "").trim().toLowerCase() === normalizedCode.toLowerCase(),
      );

      if (!partner) {
        const warning = "This BGP code was not found. Check the code before saving the subscription.";
        setBgpCodeWarning(warning);
        message.warning(warning);
        return false;
      }

      const status = String(partner.status || "").toUpperCase();
      if (!["ACTIVE", "PENDING"].includes(status)) {
        const warning = `This BGP code is ${status || "not active"} and cannot be used for this subscription.`;
        setBgpCodeWarning(warning);
        message.warning(warning);
        return false;
      }

      setBgpCodeWarning("");
      return true;
    } catch (error) {
      const warning = error?.message || "Unable to validate the BGP code. Try again before saving.";
      setBgpCodeWarning(warning);
      message.error(warning);
      return false;
    }
  };

  const createAndSelectSubscription = async (
    plan,
    subscriptionPaymentType,
    bgpCode = "",
    period = subscriptionPeriod(subscriptionPaymentType),
    couponCodes = [],
    correlationId = "",
    chargePayload = {},
  ) => {
    if (!subscriberContext.ready) {
      message.error(
        isPersonalAccount(account)
          ? "A user is required before creating a personal subscription."
          : "An organization is required before creating a business subscription.",
      );
      return false;
    }

    const createKey = `${plan.id}-${subscriptionPaymentType}`;
    const normalizedBgpCode = normalizeBgpCode(bgpCode);
    const normalizedCouponCodes = normalizeCouponCodes(couponCodesText(couponCodes));
    try {
      setCreatingPlanKey(createKey);
      const subscription = await identitySubscriptionService.subscriptions.create(
        {
          subscriberType: subscriberContext.subscriberType,
          subscriberId: subscriberContext.subscriberId,
          workspaceId,
          organizationId: subscriberContext.organizationId || undefined,
          subscriptionPlanId: plan.id,
          subscriptionPlanCode: plan.code,
          startDate: period.startDate.toISOString(),
          endDate: period.endDate.toISOString(),
          subscriptionPaymentType,
          bgpCode: normalizedBgpCode || undefined,
          couponCodes: normalizedCouponCodes.length ? normalizedCouponCodes : undefined,
          ...chargePayload,
          currency: "USD",
          status: "ACTIVE",
        },
        { workspaceId },
        correlationId,
      );
      setSubscriptions((current) => [subscription, ...current]);
      setCurrentSubscription(subscription);
      message.success("Subscription saved to the attached organization.");
      return true;
    } catch (error) {
      message.error(error?.message || "Failed to create subscription.");
      return false;
    } finally {
      setCreatingPlanKey("");
    }
  };

  const updateAndSelectSubscription = async (
    subscriptionId,
    plan,
    subscriptionPaymentType,
    bgpCode = "",
    period = subscriptionPeriod(subscriptionPaymentType),
    couponCodes = [],
    correlationId = "",
    chargePayload = {},
  ) => {
    if (!subscriptionId) return false;

    const updateKey = `${plan.id}-${subscriptionPaymentType}`;
    const normalizedBgpCode = normalizeBgpCode(bgpCode);
    const normalizedCouponCodes = normalizeCouponCodes(couponCodesText(couponCodes));
    try {
      setCreatingPlanKey(updateKey);
      const subscription = await identitySubscriptionService.subscriptions.update(
        subscriptionId,
        {
          subscriptionPlanId: plan.id,
          subscriptionPlanCode: plan.code,
          workspaceId,
          organizationId: subscriberContext.organizationId || undefined,
          startDate: period.startDate.toISOString(),
          endDate: period.endDate.toISOString(),
          subscriptionPaymentType,
          bgpCode: normalizedBgpCode || "",
          couponCodes: normalizedCouponCodes,
          ...chargePayload,
          currency: "USD",
          status: "ACTIVE",
        },
        { workspaceId },
        correlationId,
      );
      setSubscriptions((current) => [subscription, ...current.filter((item) => item?.id !== subscription?.id)]);
      setCurrentSubscription(subscription);
      message.success("Subscription saved to the attached organization.");
      return true;
    } catch (error) {
      message.error(error?.message || "Failed to update subscription.");
      return false;
    } finally {
      setCreatingPlanKey("");
    }
  };

  const findMatchingSubscription = (plan, subscriptionPaymentType, ignoredSubscriptionId = "") => {
    const planCode = planCodeOf(plan);
    const planId = planIdOf(plan);

    return availableSubscriptions.find((subscription) => {
      if (ignoredSubscriptionId && firstString(subscription?.id, subscription?.subscriptionId) === ignoredSubscriptionId) {
        return false;
      }
      const subscriptionPlanCode = firstString(
        subscription?.subscriptionPlanCode,
        subscription?.subscriptionPlan?.code,
        subscription?.plan?.code,
      );
      const subscriptionPlanId = firstString(
        subscription?.subscriptionPlanId,
        subscription?.subscriptionPlan?.id,
        subscription?.plan?.id,
      );
      const samePlan =
        (planCode && sameText(subscriptionPlanCode, planCode)) ||
        (planId && subscriptionPlanId === planId);
      return samePlan && normalizedPaymentType(subscription?.subscriptionPaymentType) === subscriptionPaymentType;
    });
  };

  const persistSelectedSubscription = async (
    ignoredSubscriptionId = "",
    bgpCode = "",
    period = subscriptionPeriod(selectedPaymentType),
    couponCodes = selectedCouponCodes,
    correlationId = "",
    chargePayload = {},
  ) => {
    if (!selectedPlan) return;
    if (isFreePlan(selectedPlan)) return;

    setCreatingPlanKey(selectedPlanCode);
    const normalizedBgpCode = normalizeBgpCode(bgpCode);
    const normalizedCouponCodes = normalizeCouponCodes(couponCodesText(couponCodes));
    const matchingSubscription = findMatchingSubscription(selectedPlan, selectedPaymentType, ignoredSubscriptionId);
    try {
      if (currentSubscriptionId && !ignoredSubscriptionId) {
        return await updateAndSelectSubscription(
          currentSubscriptionId,
          selectedPlan,
          selectedPaymentType,
          bgpCode,
          period,
          normalizedCouponCodes,
          correlationId,
          chargePayload,
        );
      }

      if (
        matchingSubscription?.id &&
        normalizeBgpCode(matchingSubscription?.bgpCode) === normalizedBgpCode &&
        couponCodesText(normalizeCouponCodes(couponCodesText(matchingSubscription?.couponCodes || []))) === couponCodesText(normalizedCouponCodes)
      ) {
        return await saveOrganizationSubscription(matchingSubscription.id);
      }

      return await createAndSelectSubscription(selectedPlan, selectedPaymentType, bgpCode, period, normalizedCouponCodes, correlationId, chargePayload);
    } finally {
      setCreatingPlanKey("");
    }
  };

  const buildPlanChangeRefundPreview = useCallback(() => {
    if (!currentSubscriptionId || !currentSubscription || currentPaymentType !== "ANNUALLY") return null;
    if (!selectedPlanCode) return null;
    if (selectedPlanCode === (currentPlanKey || effectiveCurrentPlanCode) && selectedPaymentType === currentPaymentType) return null;

    const { cycleEndDate, usedMonths } = subscriptionCycleInfo(currentSubscription.startDate);
    const currentPaidAmount = moneyNumber(currentSubscription?.amount);
    const annualAmount = moneyNumber(currentPlan?.annualAmount) || currentPaidAmount;
    const monthlyRate = moneyNumber(currentPlan?.monthlyAmount) || (annualAmount > 0 ? annualAmount / 12 : 0);
    const refundEstimate = annualAmount > 0 && monthlyRate > 0
      ? annualAmount - (monthlyRate * usedMonths)
      : 0;
    const remainingCreditAmount = refundEstimate > 0 ? money(refundEstimate) : "";
    if (!remainingCreditAmount) return null;

    const currentPlanName =
      currentSubscription.subscriptionPlanName ||
      currentPlan?.name ||
      planLabel(currentPlan, readable(currentPlanCode, "current plan"));

    return {
      refundSubscriptionId: currentSubscriptionId,
      remainingCreditValue: refundEstimate,
      remainingCreditAmount,
      remainingCreditSummary: `${remainingCreditAmount} remaining value from your current annual ${currentPlanName} plan after keeping ${usedMonths} month${usedMonths === 1 ? "" : "s"} at the monthly rate.`,
      refundDetail: "We will apply the remaining value to the new subscription after you confirm.",
      currentPlanName,
      currentPaymentType,
      currentExpiryDate: currentSubscription.endDate || "",
      refundCycleEndDate: cycleEndDate.toISOString(),
    };
  }, [
    currentPaymentType,
    currentPlan,
    currentPlanCode,
    currentPlanKey,
    currentSubscription,
    currentSubscriptionId,
    effectiveCurrentPlanCode,
    selectedPaymentType,
    selectedPlanCode,
  ]);

  const loadSubscriptionChargeBreakdown = async (amount, paymentMethod = null) => {
    const normalizedAmount = moneyValue(amount);
    if (normalizedAmount <= 0) return buildSubscriptionChargeBreakdown(0);

    try {
      const billingLocation = paymentMethodBillingLocation(paymentMethod || defaultPaymentMethod);
      if (!billingLocation.country || !billingLocation.state) {
        message.error("Default payment method is missing billing country/state. Update the card billing address before saving the subscription.");
        return null;
      }
      const result = await identitySubscriptionService.charges.effectiveCheckout({
        app: SUBSCRIPTION_CHARGE_APP,
        currency: "USD",
        stages: [SUBSCRIPTION_CHARGE_STAGE],
        country: billingLocation.country,
        state: billingLocation.state,
        chargeTarget: "CHECKOUT",
        amount: normalizedAmount,
        page: 0,
        size: 50,
      });
      return buildSubscriptionChargeBreakdown(normalizedAmount, pageContent(result));
    } catch (error) {
      message.error(error?.message || "Unable to load configured fees and taxes.");
      return null;
    }
  };

  const applySubscriptionCouponPreview = async (couponCodes = [], amount = 0) => {
    const normalizedCodes = normalizeCouponCodes(couponCodesText(couponCodes));
    const normalizedAmount = moneyValue(amount);
    if (!normalizedCodes.length || normalizedAmount <= 0) {
      return {
        originalAmount: normalizedAmount,
        totalDiscount: 0,
        discountedAmount: normalizedAmount,
        appliedCoupons: [],
        rejectedCoupons: [],
      };
    }

    try {
      const couponDetails = await Promise.all(
        normalizedCodes.map((code) => identitySubscriptionService.coupons.getByCode(code)),
      );
      const detailsByCode = new Map(
        couponDetails.map((coupon) => [String(coupon?.code || "").toUpperCase(), coupon]),
      );
      const result = await identitySubscriptionService.coupons.apply({
        couponCodes: normalizedCodes,
        userId: actorId || undefined,
        chargeItemId: currentSubscriptionId || undefined,
        chargeItemCode: "CARE_APP_SUBSCRIPTION",
        clientApp: SUBSCRIPTION_CHARGE_APP,
        currency: "USD",
        chargeAmount: normalizedAmount,
        recurringCharge: true,
        checkoutAt: new Date().toISOString(),
      });
      const rejectedCoupons = Array.isArray(result?.rejectedCoupons) ? result.rejectedCoupons : [];
      if (rejectedCoupons.length) {
        message.error(
          rejectedCoupons
            .map((coupon) => `${coupon?.code || "Coupon"}: ${coupon?.reason || "Cannot be applied"}`)
            .join("; "),
        );
        return null;
      }
      return {
        originalAmount: moneyValue(result?.originalAmount ?? normalizedAmount),
        totalDiscount: moneyValue(result?.totalDiscount),
        discountedAmount: moneyValue(result?.discountedAmount ?? normalizedAmount),
        couponDetails,
        appliedCoupons: Array.isArray(result?.appliedCoupons)
          ? result.appliedCoupons.map((coupon) => ({
              ...(detailsByCode.get(String(coupon?.code || "").toUpperCase()) || {}),
              ...coupon,
            }))
          : [],
        rejectedCoupons,
      };
    } catch (error) {
      message.error(error?.message || "Unable to get coupon details. Check the code and try again.");
      return null;
    }
  };

  const saveSelectedSubscription = async () => {
    if (subscriptionSubmitInFlightRef.current) return;
    if (!selectedPlan) return;
    if (isFreePlan(selectedPlan)) return;
    subscriptionSubmitInFlightRef.current = true;
    setCreatingPlanKey(firstString(selectedPlan?.code, selectedPlanCode) || "subscription");

    try {
      const bgpCodeIsValid = await validateBgpCodeForSubmission(subscriptionBgpCode);
      if (!bgpCodeIsValid) return;

      const planCode = firstString(selectedPlan?.code, selectedPlanCode);
      const period = subscriptionPeriod(selectedPaymentType);
      const couponCodes = normalizeCouponCodes(subscriptionCouponCodes);
      const planChargeAmount =
        selectedPaymentType === "ANNUALLY"
          ? moneyNumber(selectedPlan?.annualAmount)
          : moneyNumber(selectedPlan?.monthlyAmount);
      const couponPreview = await applySubscriptionCouponPreview(couponCodes, planChargeAmount);
      if (!couponPreview) return;
      const couponDiscountValue = moneyNumber(couponPreview.totalDiscount);
      const discountedPlanChargeAmount = moneyValue(couponPreview.discountedAmount ?? planChargeAmount);
      const planChangePreview = buildPlanChangeRefundPreview();
      const remainingCredit = moneyNumber(planChangePreview?.remainingCreditValue);
      const netBalance = planChangePreview ? remainingCredit - discountedPlanChargeAmount : 0;
      const currentChargeAmount = planChangePreview && netBalance > 0 ? 0 : planChangePreview ? Math.abs(netBalance) : discountedPlanChargeAmount;
      const needsCardCharge = currentChargeAmount > 0;
      const paymentMethod = needsCardCharge ? await ensureDefaultPaymentMethod() : null;
      if (needsCardCharge && !paymentMethod) return;
      const chargeBreakdown = needsCardCharge
        ? await loadSubscriptionChargeBreakdown(currentChargeAmount, paymentMethod)
        : buildSubscriptionChargeBreakdown(0);
      if (!chargeBreakdown) return;
      const cardSummary = paymentMethod ? maskedPaymentMethod(paymentMethod) : "No card charge";
      const cardExpiry = paymentMethodExpiry(paymentMethod);
      const billingLocation = billingLocationText(paymentMethod);
      const displayNetRefund = netBalance > 0 ? money(netBalance) : "";
      const displayNetCharge = planChangePreview && netBalance < 0 ? money(chargeBreakdown.totalCharge) : "";
      const balanceDetail = displayNetCharge
        ? `${planChangePreview.remainingCreditAmount} remaining value will be applied. Additional balance due: ${displayNetCharge}.`
        : "";
      const monthlyPlanChangeDetail =
        currentSubscriptionId && currentPaymentType === "MONTHLY" && selectedPlanCode !== (currentPlanKey || effectiveCurrentPlanCode)
          ? "Your current monthly subscription remains usable through the end of the current month. No refund is calculated for monthly subscriptions."
          : "";
      const planChangeDetail = planChangePreview
        ? [
            planChangePreview.remainingCreditSummary,
            balanceDetail ||
              (displayNetRefund
                ? `Net refundable amount after the new plan charge: ${displayNetRefund}.`
                : "The remaining value will be applied to this subscription change."),
          ].filter(Boolean).join(" ")
        : "";

      setChargePreview({
        planCode,
        planName: planLabel(selectedPlan),
        paymentType: selectedPaymentType,
        planChargeAmount: money(planChargeAmount),
        originalChargeAmount: money(planChargeAmount),
        discountedChargeAmount: money(discountedPlanChargeAmount),
        couponDiscountTotal: money(couponDiscountValue),
        couponDiscountValue,
        couponDetails: couponPreview.couponDetails || [],
        appliedCoupons: couponPreview.appliedCoupons || [],
        rejectedCoupons: couponPreview.rejectedCoupons || [],
        currentChargeAmount: money(currentChargeAmount),
        chargeAmount: displayNetRefund || displayNetCharge || money(chargeBreakdown.totalCharge),
        cardSummary,
        cardExpiry,
        billingLocation,
        serviceFee: money(chargeBreakdown.serviceFee),
        oliveebServiceFee: money(chargeBreakdown.oliveebServiceFee),
        oliveebServiceFeeRate: ratePercentText(chargeBreakdown.oliveebServiceFeeRate),
        federalTax: money(chargeBreakdown.federalTax),
        federalTaxRate: ratePercentText(chargeBreakdown.federalTaxRate),
        stateTax: money(chargeBreakdown.stateTax),
        stateTaxRate: ratePercentText(chargeBreakdown.stateTaxRate),
        paymentProcessorFee: money(chargeBreakdown.totalPaymentProcessorFee),
        paymentProcessorFeeRate: ratePercentText(chargeBreakdown.paymentProcessorFeeRate),
        subtotalBeforeProcessor: money(chargeBreakdown.chargeableAmount),
        totalCharge: money(chargeBreakdown.totalCharge),
        chargeLines: chargeBreakdown.chargeLines || [],
        chargeBreakdown,
        refundAmount: displayNetRefund,
        netRefundAmount: displayNetRefund,
        netChargeAmount: displayNetCharge,
        annualSavings: planAnnualSavings(selectedPlan),
        bgpCode: selectedBgpCode || undefined,
        couponCodes,
        startDate: period.startDate.toISOString(),
        endDate: period.endDate.toISOString(),
        correlationId: newCorrelationId(),
        detail:
          planChangeDetail ||
          monthlyPlanChangeDetail ||
          (selectedPaymentType === "MONTHLY"
            ? "Monthly billing charges one month now."
            : "Annual billing charges the full annual amount now."),
        refundSummary: displayNetRefund
          ? `Estimated net refund after applying ${planChangePreview.remainingCreditAmount} remaining value: ${displayNetRefund}.`
          : "",
        ...(planChangePreview || {}),
      });
    } finally {
      subscriptionSubmitInFlightRef.current = false;
      setCreatingPlanKey("");
    }
  };

  const confirmChargePreview = async () => {
    if (!chargePreview || subscriptionSubmitInFlightRef.current) return;
    subscriptionSubmitInFlightRef.current = true;
    setCreatingPlanKey(chargePreview.planCode || selectedPlanCode || "subscription");

    try {
      const bgpCodeIsValid = await validateBgpCodeForSubmission(subscriptionBgpCode);
      if (!bgpCodeIsValid) return;
      const requiresCardCharge = moneyNumber(chargePreview.totalCharge || chargePreview.currentChargeAmount) > 0;
      if (requiresCardCharge) {
        const paymentMethod = await ensureDefaultPaymentMethod();
        if (!paymentMethod) return;
      }
      const period = {
        startDate: new Date(chargePreview.startDate),
        endDate: new Date(chargePreview.endDate),
      };
      const correlationId = chargePreview.correlationId || newCorrelationId();
      const couponCodes = normalizeCouponCodes(subscriptionCouponCodes).length
        ? normalizeCouponCodes(subscriptionCouponCodes)
        : chargePreview.couponCodes || [];
      if (couponCodesText(couponCodes) !== couponCodesText(chargePreview.couponCodes || [])) {
        message.info("Coupon changed. Review the updated total before confirming.");
        setChargePreview(null);
        subscriptionSubmitInFlightRef.current = false;
        setCreatingPlanKey("");
        await saveSelectedSubscription();
        return;
      }
      const saved = await persistSelectedSubscription(
        "",
        normalizeBgpCode(subscriptionBgpCode) || chargePreview.bgpCode || "",
        period,
        couponCodes,
        correlationId,
        subscriptionChargePayload(chargePreview),
      );
      if (saved) {
        setChargePreview(null);
        setSubscriptionBgpCode("");
        setSubscriptionCouponCodes("");
        window.location.reload();
      }
    } catch (error) {
      message.error(error?.message || "Unable to update subscription.");
    } finally {
      subscriptionSubmitInFlightRef.current = false;
      setCreatingPlanKey("");
    }
  };

  const buildCancelPreview = useCallback(() => {
    if (!currentSubscriptionId || !currentSubscription) return null;

    const paymentType = currentPaymentType === "ANNUALLY" ? "ANNUALLY" : "MONTHLY";
    const { cycleEndDate } = subscriptionCycleInfo(currentSubscription.startDate);
    const planName =
      currentSubscription.subscriptionPlanName ||
      currentPlan?.name ||
      planLabel(currentPlan, readable(currentPlanCode, "current plan"));
    const monthlyRate = moneyNumber(currentPlan?.monthlyAmount);
    const annualAmount = moneyNumber(currentPlan?.annualAmount);
    const refundEstimate = paymentType === "ANNUALLY" ? annualAmount : monthlyRate;
    const refundAmount = refundEstimate > 0 ? money(refundEstimate) : "";

    return {
      subscriptionId: currentSubscriptionId,
      planName,
      paymentType,
      currentExpiryDate: currentSubscription.endDate || "",
      newExpiryDate: cycleEndDate.toISOString(),
      refundAmount,
      refundSummary: refundAmount
        ? `${refundAmount} estimated refund for the current ${paymentType === "ANNUALLY" ? "annual" : "monthly"} subscription.`
        : `No refundable ${paymentType === "ANNUALLY" ? "annual" : "monthly"} amount was found.`,
      detail:
        "Your subscription will continue working through the end of the current subscription month. We will submit the current paid subscription amount for refund handling after you confirm.",
    };
  }, [currentPaymentType, currentPlan, currentPlanCode, currentSubscription, currentSubscriptionId]);

  const handleCurrentSubscriptionLifecycle = () => {
    if (!currentSubscriptionId || creatingPlanKey) return;
    const preview = buildCancelPreview();
    if (preview) setCancelPreview(preview);
  };

  const confirmCancelPreview = async () => {
    if (!cancelPreview || creatingPlanKey) return;

    try {
      setCreatingPlanKey(currentPlanCode || cancelPreview.subscriptionId);
      await identitySubscriptionService.subscriptions.cancel(cancelPreview.subscriptionId);
      setCancelPreview(null);
      message.success("Subscription cancelled.");
      await loadData();
    } catch (error) {
      message.error(error?.message || "Unable to cancel subscription.");
    } finally {
      setCreatingPlanKey("");
    }
  };

  const renderPlan = (plan) => {
    const planCode = planCodeOf(plan);
    const planId = planIdOf(plan);
    const planName = planNameOf(plan);
    const key = planKey(plan);
    const freePlan = isFreePlan(plan);
    const selected = Boolean(!freePlan && key && key === selectedPlanCode);
    const isCurrent = Boolean(
      (currentSubscription &&
        ((currentPlanId && planId === currentPlanId) ||
          (currentPlanCode && sameText(planCode, currentPlanCode)) ||
          (currentPlanName && sameText(planName, currentPlanName)))) ||
        (includedPlanIsActive && freePlan && planCode === effectiveCurrentPlanCode),
    );
    const planPaymentType =
      selectedPaymentTypes[key] ||
      (key === currentPlanKey && currentPaymentType ? currentPaymentType : "MONTHLY");
    const annualSavings = planAnnualSavings(plan);
    const benefits = planBenefits(plan, account);
    const planSignalText = planSignal(plan);
    const includesAnnualWebsite =
      !isPersonalAccount(account) &&
      (planSignalText.includes("STANDARD") || planSignalText.includes("PREMIUM"));
    const billingButton = (label, value) => {
      const currentBilling = isCurrent && value === currentPaymentType;
      const selectedBilling = selected && planPaymentType === value && !currentBilling;

      return (
        <Button
          block
          key={value}
          onClick={(event) => {
            event.stopPropagation();
            setSelectedPlanCode(key);
            setSelectedPaymentTypes((current) => ({
              ...current,
              [key]: value,
            }));
          }}
          style={{
            borderColor: currentBilling
              ? OLIVEEB_COLORS.oliveebGreen
              : selectedBilling
                ? OLIVEEB_COLORS.oliveebOrange
                : OLIVEEB_COLORS.border,
            background: currentBilling
              ? OLIVEEB_COLORS.oliveebGreen
              : selectedBilling
                ? OLIVEEB_COLORS.oliveebOrange
                : OLIVEEB_COLORS.white,
            color: currentBilling || selectedBilling ? OLIVEEB_COLORS.white : OLIVEEB_COLORS.textPrimary,
            fontWeight: currentBilling || selectedBilling ? 800 : 600,
          }}
        >
          {label}
        </Button>
      );
    };

    return (
      <div className="workspace-subscription-plan-column">
        <Card
          size="small"
          hoverable={!freePlan}
          onClick={() => {
            if (!freePlan) setSelectedPlanCode(key);
          }}
          style={{
            height: "100%",
            borderRadius: 8,
            overflow: "hidden",
            borderColor: isCurrent
              ? OLIVEEB_COLORS.oliveebGreen
              : selected
                ? OLIVEEB_COLORS.oliveebOrange
                : OLIVEEB_COLORS.oliveebBlueSoft,
            borderWidth: selected || isCurrent ? 2 : 1,
            background: isCurrent
              ? OLIVEEB_COLORS.greenSoft2
              : selected
                ? "#FFF3E8"
                : OLIVEEB_COLORS.card,
            boxShadow: isCurrent
              ? "0 0 0 3px rgba(39, 174, 48, 0.16), 0 14px 28px rgba(39, 174, 48, 0.12)"
              : selected
                ? "0 0 0 3px rgba(230, 126, 34, 0.14)"
                : "none",
          }}
          bodyStyle={{ position: "relative", height: "100%" }}
        >
          <div
            style={{
              position: "absolute",
              inset: "0 0 auto",
              height: 4,
              background: isCurrent
                ? OLIVEEB_COLORS.oliveebGreen
                : selected
                  ? OLIVEEB_COLORS.oliveebOrange
                  : OLIVEEB_COLORS.goldBorder,
            }}
          />
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div style={{ minWidth: 0 }}>
                <Text strong style={{ display: "block", wordBreak: "break-word" }}>
                  {planLabel(plan)}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {planDescription(plan)}
                </Text>
              </div>
              <Space size={4} wrap style={{ justifyContent: "flex-end" }}>
                {freePlan ? (
                  <Tag
                    color={isCurrent ? "green" : "default"}
                    style={{ fontWeight: 800, fontSize: 13 }}
                  >
                    Included
                  </Tag>
                ) : null}
                {isCurrent ? (
                  <Tag color="green" style={{ fontWeight: 800, fontSize: 13 }}>
                    Current
                  </Tag>
                ) : null}
                {selected && !isCurrent ? (
                  <Tag color="gold" style={{ fontWeight: 800, fontSize: 13 }}>
                    Selected
                  </Tag>
                ) : null}
              </Space>
            </div>

            {freePlan ? (
              <div
                style={{
                  border: `1px solid ${isCurrent ? OLIVEEB_COLORS.oliveebGreen : OLIVEEB_COLORS.border}`,
                  borderRadius: 8,
                  padding: "10px 12px",
                  background: isCurrent ? OLIVEEB_COLORS.greenSoft2 : OLIVEEB_COLORS.bgSoft,
                  color: OLIVEEB_COLORS.oliveebDarkGreen,
                  fontWeight: isCurrent ? 700 : 500,
                }}
              >
                Included when no paid subscription is active.
              </div>
            ) : (
              <>
                <Space.Compact block>
                  {billingButton(`Monthly ${planMonthlyPrice(plan)}`, "MONTHLY")}
                  {billingButton(`Annual ${planAnnualPrice(plan)}`, "ANNUALLY")}
                </Space.Compact>
                {planPaymentType === "MONTHLY" ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Annual total if paid monthly: {planAnnualizedMonthlyPrice(plan)}
                  </Text>
                ) : null}
                {annualSavings ? (
                  <Text style={{ color: OLIVEEB_COLORS.oliveebGreen, fontSize: 12 }}>
                    {planPaymentType === "MONTHLY"
                      ? `Save ${annualSavings} by choosing annual billing`
                      : `You save ${annualSavings} with annual billing`}
                  </Text>
                ) : null}
                {includesAnnualWebsite ? (
                  <Text style={{ color: OLIVEEB_COLORS.oliveebBlue, fontSize: 12, fontWeight: 700 }}>
                    Annual billing includes a free business website.
                  </Text>
                ) : null}
                {isCurrent ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Active {readable(currentPaymentType, "subscription")} through{" "}
                    {formatDate(currentSubscription?.endDate) || "-"}
                  </Text>
                ) : null}
                {isCurrent && currentSubscription?.bgpCode ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    BGP Code: <Text code>{currentSubscription.bgpCode}</Text>
                  </Text>
                ) : null}
                {isCurrent && currentCouponCodes.length ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Coupons: <Text code>{couponCodesText(currentCouponCodes)}</Text>
                  </Text>
                ) : null}
                {selected ? (
                  <Tag
                    color={hasSaveChanges && selected ? "gold" : "default"}
                    style={{ fontWeight: 800, fontSize: 13 }}
                  >
                    {isCurrent ? "Current selection" : "Ready to save"}
                  </Tag>
                ) : null}
                {selected ? (
                  <div
                    onClick={(event) => event.stopPropagation()}
                    style={{
                      border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                      borderRadius: 8,
                      padding: 10,
                      background: OLIVEEB_COLORS.white,
                    }}
                  >
                    <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue, fontSize: 13 }}>
                      BGP Code (Referral Code)
                    </Text>
                    <Input
                      value={subscriptionBgpCode}
                      onChange={(event) => {
                        setSubscriptionBgpCode(event.target.value);
                        setBgpCodeWarning("");
                      }}
                      placeholder="Optional Business Growth Partner code"
                      maxLength={80}
                      autoComplete="off"
                      style={{
                        marginTop: 5,
                        borderColor: OLIVEEB_COLORS.oliveebGreen,
                        borderRadius: 14,
                        boxShadow: `0 10px 22px ${OLIVEEB_COLORS.oliveebGreen}22`,
                      }}
                    />
                    {bgpCodeWarning ? (
                      <Text type="danger" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                        {bgpCodeWarning}
                      </Text>
                    ) : null}
                    <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue, display: "block", fontSize: 13, marginTop: 12 }}>
                      Coupon Codes
                    </Text>
                    <Input
                      value={subscriptionCouponCodes}
                      onChange={(event) => setSubscriptionCouponCodes(event.target.value.toUpperCase())}
                      placeholder="Optional codes, comma separated"
                      maxLength={96}
                      autoComplete="off"
                      style={{
                        marginTop: 5,
                        borderColor: OLIVEEB_COLORS.oliveebBlue,
                        borderRadius: 14,
                        boxShadow: `0 10px 22px ${OLIVEEB_COLORS.oliveebBlue}1f`,
                      }}
                    />
                    {selectedCouponCodes.length ? (
                      <Space wrap size={[4, 4]} style={{ marginTop: 8 }}>
                        {selectedCouponCodes.map((code) => (
                          <Tag key={code} color="purple" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
                            {code}
                          </Tag>
                        ))}
                      </Space>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
            {benefits.length ? (
              <div
                style={{
                  borderTop: `1px solid ${OLIVEEB_COLORS.border}`,
                  paddingTop: 10,
                  marginTop: 2,
                }}
              >
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen, fontSize: 13 }}>
                  Main services included
                </Text>
                <Space direction="vertical" size={6} style={{ width: "100%", marginTop: 8 }}>
                  {benefits.map((benefit) => (
                    <div
                      key={benefit}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 7,
                        color: OLIVEEB_COLORS.textPrimary,
                        fontSize: 12,
                        lineHeight: 1.35,
                      }}
                    >
                      <CheckCircleOutlined
                        style={{
                          color: OLIVEEB_COLORS.oliveebGreen,
                          fontSize: 13,
                          marginTop: 2,
                          flex: "0 0 auto",
                        }}
                      />
                      <span>{benefit}</span>
                    </div>
                  ))}
                </Space>
              </div>
            ) : null}
          </Space>
        </Card>
      </div>
    );
  };

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="credit-card"
          title="Workspace Subscriptions"
          subtitle="Select the subscription this workspace should use"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            style={workspaceButtonStyles.back}
            onClick={() => navigate(paths.workspaceDetails(workspaceId))}
          >
            Back
          </Button>
          {account && (isPersonalAccount(account) || account.organizationId) ? (
            <Button
              icon={<WalletOutlined />}
              style={workspaceButtonStyles.paymentMethods}
              onClick={() => navigate(paths.workspacePaymentMethods(workspaceId))}
            >
              Payment methods
            </Button>
          ) : null}
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!isPersonalAccount(account) && !account.organizationId ? (
            <Alert
              type="warning"
              showIcon
              message={
                <Text style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 800 }}>
                  This workspace is not linked to an organization.
                </Text>
              }
              description={
                <Space direction="vertical" size={8} align="start">
                  <Text style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
                    Business subscriptions can only be created after an organization is linked to the workspace.
                  </Text>
                  <Button
                    size="small"
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => navigate(paths.workspaceOrganization(workspaceId))}
                    style={{
                      background: OLIVEEB_COLORS.oliveebBlue,
                      borderColor: OLIVEEB_COLORS.oliveebBlue,
                      color: OLIVEEB_COLORS.white,
                      fontWeight: 800,
                    }}
                  >
                    Link organization
                  </Button>
                </Space>
              }
              style={{ textAlign: "left" }}
            />
          ) : null}

          <Card
            size="small"
            style={{
              borderRadius: 8,
              borderColor: OLIVEEB_COLORS.oliveebGreenSoft,
              background: OLIVEEB_COLORS.greenSoft2,
            }}
            bodyStyle={{ padding: "10px 12px" }}
          >
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <Space size={8} wrap>
                <CreditCardOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} />
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Current subscription
                </Text>
                <Text>
                  {currentSubscription
                    ? subscriptionLabel(currentSubscription)
                    : planLabel(defaultFreePlan, "Included subscription")}
                </Text>
              </Space>
              <Space size={6} wrap>
                {currentSubscription ? (
                  <>
                    <Tag color="purple">
                      {readable(currentSubscription.subscriptionPaymentType, "Monthly")}
                    </Tag>
                    <Tag color="green">{readable(currentSubscription.status, "Active")}</Tag>
                    {currentSubscription.bgpCode ? (
                      <Tag color="cyan" style={{ fontWeight: 700 }}>
                        BGP Code: {currentSubscription.bgpCode}
                      </Tag>
                    ) : null}
                    {currentCouponCodes.map((code) => (
                      <Tag key={code} color="purple" style={{ fontWeight: 700 }}>
                        Coupon: {code}
                      </Tag>
                    ))}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Started {formatDate(currentSubscription.startDate) || "-"}
                    </Text>
                    {currentSubscriptionId ? (
                      <Button
                        size="small"
                        danger
                        loading={Boolean(creatingPlanKey)}
                        onClick={handleCurrentSubscriptionLifecycle}
                      >
                        Cancel subscription
                      </Button>
                    ) : null}
                  </>
                ) : (
                  <Tag color="green" style={{ fontWeight: 800 }}>
                    Included
                  </Tag>
                )}
              </Space>
            </div>
          </Card>

          <Card
            title={sectionTitle("Subscription Options")}
            extra={<Tag color="green">{plans.length} options</Tag>}
            style={{ borderRadius: 8 }}
          >
            <div
              className="d-flex justify-content-between align-items-center gap-3 flex-wrap"
              style={{
                border: `1px solid ${OLIVEEB_COLORS.border}`,
                borderRadius: 8,
                padding: 12,
                marginBottom: 16,
                background: OLIVEEB_COLORS.bgSoft,
              }}
            >
              <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                <Text strong style={{ display: "block", color: OLIVEEB_COLORS.oliveebDarkGreen }}>
                  Subscription selection
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {selectedPlan
                    ? `Selected: ${planLabel(selectedPlan)}${
                        isFreePlan(selectedPlan) ? "" : ` (${readable(selectedPaymentType, "Monthly")})`
                      }`
                    : "Choose a paid subscription option to save it for the attached organization."}
                </Text>
                {selectedCouponCodes.length ? (
                  <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Coupons:</Text>
                    {selectedCouponCodes.map((code) => (
                      <Tag key={code} color="purple" style={{ marginInlineEnd: 0, fontWeight: 700 }}>
                        {code}
                      </Tag>
                    ))}
                  </Space>
                ) : null}
              </div>
              <Button
                type="primary"
                icon={<CreditCardOutlined />}
                disabled={!hasSaveChanges}
                loading={Boolean(creatingPlanKey) || checkingPaymentMethod}
                onClick={saveSelectedSubscription}
              >
                Save Subscription
              </Button>
            </div>
            {plans.length ? (
              <>
                <div
                  className="workspace-subscription-plan-grid"
                  style={{ "--workspace-subscription-plan-count": plans.length }}
                >
                  {plans.map((plan) => (
                    <div key={planKey(plan) || planLabel(plan)}>
                      {renderPlan(plan)}
                    </div>
                  ))}
                </div>
                <div className="d-flex justify-content-end mt-2">
                  <Button
                    type="primary"
                    icon={<CreditCardOutlined />}
                    disabled={!hasSaveChanges}
                    loading={Boolean(creatingPlanKey) || checkingPaymentMethod}
                    onClick={saveSelectedSubscription}
                  >
                    Save Subscription
                  </Button>
                </div>
              </>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No active subscription options found"
              />
            )}
          </Card>

        </Space>
      )}

      <Modal
        open={Boolean(chargePreview)}
        title="Confirm subscription update"
        okText="Confirm and save"
        cancelText="Back"
        confirmLoading={Boolean(creatingPlanKey) || checkingPaymentMethod}
        onOk={confirmChargePreview}
        onCancel={() => {
          setChargePreview(null);
        }}
      >
        {chargePreview ? (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Alert
              type={chargePreview.remainingCreditAmount ? "info" : "success"}
              showIcon
              message={
                chargePreview.netRefundAmount
                  ? `Estimated net refund: ${chargePreview.netRefundAmount}`
                  : chargePreview.netChargeAmount
                    ? `Estimated net charge: ${chargePreview.netChargeAmount}`
                    : `Estimated charge: ${chargePreview.chargeAmount}`
              }
              description={chargePreview.refundSummary || chargePreview.detail}
            />
            <div
              style={{
                border: `1px solid ${OLIVEEB_COLORS.border}`,
                borderRadius: 8,
                padding: 12,
                background: OLIVEEB_COLORS.bgSoft,
              }}
            >
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">New plan</Text>
                  <Text strong>{chargePreview.planName}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Billing</Text>
                  <Text>{readable(chargePreview.paymentType, "Monthly")}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Plan charge</Text>
                  <Text>{chargePreview.planChargeAmount}</Text>
                </div>
                {moneyNumber(chargePreview.couponDiscountTotal) > 0 ? (
                  <>
                    {(chargePreview.appliedCoupons || []).map((coupon) => (
                      <div className="d-flex justify-content-between gap-3" key={coupon?.id || coupon?.code}>
                        <Text type="secondary">Coupon discount</Text>
                        <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                          {couponDescription(coupon)}
                        </Text>
                      </div>
                    ))}
                    <div className="d-flex justify-content-between gap-3">
                      <Text type="secondary">Amount after coupon</Text>
                      <Text strong>{chargePreview.discountedChargeAmount}</Text>
                    </div>
                  </>
                ) : null}
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Card charged</Text>
                  <span className="text-end">
                    <Text strong>{chargePreview.cardSummary}</Text>
                    {chargePreview.cardExpiry ? (
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        Exp {chargePreview.cardExpiry}
                      </Text>
                    ) : null}
                    {chargePreview.billingLocation ? (
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        Billing {chargePreview.billingLocation}
                      </Text>
                    ) : null}
                  </span>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Starts</Text>
                  <Text>{formatDate(chargePreview.startDate) || "-"}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Ends</Text>
                  <Text>{formatDate(chargePreview.endDate) || "-"}</Text>
                </div>
                {selectedBgpCode ? (
                  <div className="d-flex justify-content-between gap-3">
                    <Text type="secondary">BGP Code</Text>
                    <Text code>{selectedBgpCode}</Text>
                  </div>
                ) : null}
                <div>
                  <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
                    Coupon codes
                  </Text>
                  <Input
                    value={subscriptionCouponCodes}
                    onChange={(event) => setSubscriptionCouponCodes(event.target.value.toUpperCase())}
                    placeholder="Optional codes, comma separated"
                    maxLength={96}
                    autoComplete="off"
                  />
                </div>
                {(normalizeCouponCodes(subscriptionCouponCodes).length || chargePreview.couponCodes?.length) ? (
                  <div className="d-flex justify-content-between gap-3">
                    <Text type="secondary">Coupons</Text>
                    <Text code>
                      {couponCodesText(
                        normalizeCouponCodes(subscriptionCouponCodes).length
                          ? normalizeCouponCodes(subscriptionCouponCodes)
                          : chargePreview.couponCodes,
                      )}
                    </Text>
                  </div>
                ) : null}
                {chargePreview.remainingCreditAmount ? (
                  <div className="d-flex justify-content-between gap-3">
                    <Text type="secondary">Refundable current value</Text>
                    <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                      {chargePreview.remainingCreditAmount}
                    </Text>
                  </div>
                ) : null}
                {chargePreview.remainingCreditSummary ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {chargePreview.remainingCreditSummary}
                  </Text>
                ) : null}
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Subscription amount due</Text>
                  <Text>{chargePreview.currentChargeAmount}</Text>
                </div>
                {sortedChargeLines(chargePreview.chargeLines).map((line, index) => (
                  <div
                    className="d-flex justify-content-between gap-3"
                    key={firstString(line?.chargeId, line?.id, line?.code, `${index}`)}
                  >
                    <Text type="secondary">{chargeLineLabel(line)}</Text>
                    <Text>{money(line.amount)}</Text>
                  </div>
                ))}
                <div className="d-flex justify-content-between gap-3">
                  <Text strong>Estimated Taxes &amp; Fees</Text>
                  <Text strong>{money(feesAndTaxesTotal(chargePreview.chargeLines))}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text strong>Total charged to card</Text>
                  <Text strong>{chargePreview.totalCharge}</Text>
                </div>
                {chargePreview.annualSavings ? (
                  <Text style={{ color: OLIVEEB_COLORS.oliveebGreen, fontSize: 12 }}>
                    Annual savings: {chargePreview.annualSavings}
                  </Text>
                ) : null}
              </Space>
            </div>
          </Space>
        ) : null}
      </Modal>

      <Modal
        open={paymentMethodDialogOpen}
        title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Add payment method</Text>}
        okText="Add Payment Method"
        cancelText="Not now"
        confirmLoading={addingPaymentMethod}
        okButtonProps={{
          disabled: !stripeReady || !cardComplete || Boolean(stripeError),
          style: {
            borderColor: OLIVEEB_COLORS.oliveebGreen,
            background: OLIVEEB_COLORS.oliveebGreen,
            color: OLIVEEB_COLORS.white,
          },
        }}
        onOk={addQuickPaymentMethod}
        onCancel={closePaymentMethodDialog}
      >
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Alert
            type={stripePublishableKey ? "warning" : "error"}
            showIcon
            message="A default payment method is required before saving this subscription."
            description={
              stripePublishableKey
                ? "Add a card here, then save the subscription again. You can also open payment methods to manage saved cards."
                : "Stripe card capture is not configured for this environment. Open payment methods to add or select a default payment method."
            }
          />

          <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue }}>
            Quick payment capture
          </Text>
          <div
            style={{
              padding: "15px 16px",
              border: `1px solid ${stripeError ? OLIVEEB_COLORS.oliveebRed : OLIVEEB_COLORS.oliveebGreen}`,
              borderRadius: 8,
              background: OLIVEEB_COLORS.card,
              boxShadow: "0 14px 28px rgba(15, 23, 42, 0.1)",
            }}
          >
            <div ref={cardMountRef} />
            {!stripeReady && stripePublishableKey ? (
              <Text type="secondary">Loading secure card field...</Text>
            ) : null}
          </div>
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
              Billing address
            </Text>
            {appConfigLoading && !googleMapsApiKey ? (
              <Input disabled placeholder="Loading address search..." />
            ) : (
              <GooglePlacesAutocompleteInput
                googleMapsApiKey={googleMapsApiKey}
                placeholder={googleMapsApiKey ? "Search billing address" : "Enter billing address"}
                types={["address"]}
                value={billingAddressSearch}
                onChange={setBillingAddressSearch}
                onResolvePlace={applyBillingPlace}
              />
            )}
          </div>
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
              Street address
            </Text>
            <Input
              value={billingAddressLine1}
              onChange={(event) => setBillingAddressLine1(normalizeBillingText(event.target.value))}
              placeholder="123 Main St"
              autoComplete="billing address-line1"
            />
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <div style={{ flex: "1 1 180px" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
                City
              </Text>
              <Input
                value={billingCity}
                onChange={(event) => setBillingCity(normalizeBillingText(event.target.value, 80))}
                placeholder="Seattle"
                autoComplete="billing address-level2"
              />
            </div>
            <div style={{ flex: "1 1 180px" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
                County
              </Text>
              <Input
                value={billingCounty}
                onChange={(event) => setBillingCounty(normalizeBillingText(event.target.value, 120))}
                placeholder="King"
                autoComplete="billing address-level3"
              />
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <div style={{ flex: "1 1 120px" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
                Country
              </Text>
              <Input
                value={billingCountry}
                onChange={(event) => setBillingCountry(normalizeBillingCountry(event.target.value))}
                placeholder="US"
                maxLength={2}
                autoComplete="billing country"
              />
            </div>
            <div style={{ flex: "1 1 120px" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
                State
              </Text>
              <Input
                value={billingState}
                onChange={(event) => setBillingState(normalizeBillingState(event.target.value))}
                placeholder="WA"
                maxLength={3}
                autoComplete="billing address-level1"
              />
            </div>
            <div style={{ flex: "1 1 140px" }}>
              <Text type="secondary" style={{ display: "block", marginBottom: 5 }}>
                ZIP
              </Text>
              <Input
                value={billingPostalCode}
                onChange={(event) => setBillingPostalCode(normalizeBillingPostalCode(event.target.value))}
                placeholder="98101"
                maxLength={12}
                autoComplete="billing postal-code"
              />
            </div>
          </div>
          {stripeError ? (
            <Text type="danger">{stripeError}</Text>
          ) : (
            <Text type="secondary">
              Card details are captured securely through Stripe.
            </Text>
          )}
          <Button
            block
            icon={<WalletOutlined />}
            style={workspaceButtonStyles.paymentMethods}
            onClick={() => navigate(paths.workspacePaymentMethods(workspaceId))}
          >
            Manage payment methods
          </Button>
        </Space>
      </Modal>

      <Modal
        open={Boolean(cancelPreview)}
        title="Cancel subscription"
        okText="Confirm cancellation"
        okButtonProps={{ danger: true }}
        cancelText="Back"
        confirmLoading={Boolean(creatingPlanKey)}
        onOk={confirmCancelPreview}
        onCancel={() => setCancelPreview(null)}
      >
        {cancelPreview ? (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Alert
              type="warning"
              showIcon
              message={cancelPreview.refundSummary}
              description={cancelPreview.detail}
            />
            <div
              style={{
                border: `1px solid ${OLIVEEB_COLORS.border}`,
                borderRadius: 8,
                padding: 12,
                background: OLIVEEB_COLORS.bgSoft,
              }}
            >
              <Space direction="vertical" size={6} style={{ width: "100%" }}>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Current plan</Text>
                  <Text strong>{cancelPreview.planName}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Billing</Text>
                  <Text>{readable(cancelPreview.paymentType, "Monthly")}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Current expiry</Text>
                  <Text>{formatDate(cancelPreview.currentExpiryDate) || "-"}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Continues until</Text>
                  <Text>{formatDate(cancelPreview.newExpiryDate) || "-"}</Text>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <Text type="secondary">Refund estimate</Text>
                  <Text style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                    {cancelPreview.refundAmount || "-"}
                  </Text>
                </div>
              </Space>
            </div>
          </Space>
        ) : null}
      </Modal>
    </div>
  );
};

export default WorkspaceSubscriptionsPage;
